import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { Icon } from "../components/Icon.tsx";
import { Quantity } from "../components/Options.tsx";
import { useConfig } from "../components/Shell.tsx";
import { api, call } from "../lib/api.ts";
import { cartStore, keyOf, useCartItems } from "../lib/cart.ts";
import { money } from "../lib/format.ts";

/** Server-priced view of the bag: the browser's cached prices are only a first paint. */
export function usePricedCart() {
  const items = useCartItems();
  const lines = items.map((i) => i.line);
  return {
    items,
    priced: useQuery({
      queryKey: ["cart-price", lines],
      enabled: lines.length > 0,
      queryFn: () => call(api.cart.price.$post({ json: { lines } })),
      placeholderData: (prev) => prev,
    }),
  };
}

export function BagPage() {
  const cfg = useConfig();
  const currency = cfg.data?.currency ?? "EUR";
  const { items, priced } = usePricedCart();

  if (items.length === 0) {
    return (
      <div className="sheet sheet--narrow emptystate">
        <div className="emptystate__slot" aria-hidden="true" />
        <h1 className="page-title">Your bag is an empty sheet</h1>
        <p className="lede">Peel something off the shop, or make a meme nobody else has.</p>
        <div className="emptystate__actions">
          <Link to="/make" className="btn">
            <Icon name="pen" /> Make your own
          </Link>
          <Link to="/shop" className="btn btn--white">
            Shop ready-made
          </Link>
        </div>
      </div>
    );
  }

  const server = priced.data?.lines;
  const subtotal =
    priced.data?.subtotalCents ?? items.reduce((s, i) => s + i.unitPriceCents * i.line.quantity, 0);
  const blocked = server?.some((l) => !l.available) ?? false;

  return (
    <div className="sheet bag">
      <h1 className="page-title">Your bag</h1>
      <div className="bag__layout">
        <ul className="baglist">
          {items.map((item, i) => {
            const s = server?.[i];
            const unit = s?.available ? s.unitPriceCents : item.unitPriceCents;
            return (
              <li
                key={keyOf(item.line)}
                className="bagline"
                data-unavailable={s ? !s.available : undefined}
              >
                <Link to={item.href} className="bagline__thumb">
                  {item.imageUrl ? <img src={item.imageUrl} alt="" /> : null}
                </Link>
                <div className="bagline__info">
                  <Link to={item.href} className="bagline__title">
                    {item.title}
                  </Link>
                  <span className="bagline__variant">{item.variantTitle}</span>
                  {s && !s.available && <span className="form-error">{s.unavailableReason}</span>}
                  <div className="bagline__controls">
                    <Quantity
                      value={item.line.quantity}
                      onChange={(n) => cartStore.setQuantity(i, n)}
                      label={`Quantity of ${item.title}`}
                    />
                    <button type="button" className="link-btn" onClick={() => cartStore.remove(i)}>
                      Remove
                    </button>
                  </div>
                </div>
                <span className="bagline__price price tabular">
                  {money(unit * item.line.quantity, currency)}
                </span>
              </li>
            );
          })}
        </ul>
        <aside className="panel bag__summary" aria-label="Summary">
          <dl className="totals">
            <div>
              <dt>Subtotal</dt>
              <dd className="tabular">{money(subtotal, currency)}</dd>
            </div>
            <div>
              <dt>Shipping</dt>
              <dd>At checkout</dd>
            </div>
          </dl>
          {blocked && <p className="form-error">Remove the unavailable items to continue.</p>}
          <Link
            to="/checkout"
            className="btn btn--block"
            aria-disabled={blocked}
            onClick={(e) => blocked && e.preventDefault()}
          >
            <Icon name="lock" /> Checkout
          </Link>
          <p className="bag__note">Pay with PayPal: balance, cards and more.</p>
        </aside>
      </div>
    </div>
  );
}
